import { Lock, Settings, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { toast } from "@/components/ui/sonner";

interface PaywallOverlayProps {
  featureName: string;
  minPlan?: string;
}

export const PaywallOverlay = ({ featureName, minPlan = "Inicial" }: PaywallOverlayProps) => {
  const navigate = useNavigate();
  const [loadingPortal, setLoadingPortal] = useState(false);

  const handleManageSubscription = async () => {
    setLoadingPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.url) {
        const win = window.open(data.url, "_blank");
        if (!win) window.location.href = data.url;
      }
    } catch (err) {
      console.error("Error opening customer portal:", err);
      toast.error("Não foi possível abrir o portal. Tente novamente.");
    } finally {
      setLoadingPortal(false);
    }
  };

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/60 backdrop-blur-[2px]">
      <Card className="w-full max-w-md mx-4 shadow-xl border-2 border-primary/20">
        <CardHeader className="text-center space-y-3">
          <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="w-7 h-7 text-primary" />
          </div>
          <CardTitle className="text-2xl">Assinatura Necessária</CardTitle>
          <CardDescription className="text-base">
            Para acessar {featureName.toLowerCase()}, você precisa de um plano ativo
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 rounded-lg bg-muted/50 text-center">
            <p className="text-sm text-muted-foreground">
              Escolha um plano a partir de <span className="font-semibold text-foreground">{minPlan}</span>
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <Button 
              onClick={() => navigate("/planos")} 
              className="w-full"
              size="lg"
            >
              Ver Planos e Contratar
            </Button>
            <Button 
              onClick={handleManageSubscription}
              variant="outline"
              size="lg"
              className="w-full"
              disabled={loadingPortal}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              {loadingPortal ? "Abrindo..." : "Gerenciar Assinatura"}
            </Button>
            <Button 
              onClick={() => navigate("/settings")} 
              variant="ghost"
              size="sm"
              className="w-full"
            >
              <Settings className="w-4 h-4 mr-2" />
              Ir para Configurações
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
